
import { useState, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getPrivacyStatus } from "@/api/auth";
import { LoadingSpinner } from "./LoadingSpinner";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAuthLoading, subscription, isSubscriptionLoading, checkSubscriptionStatus } = useAuth();
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState<boolean | null>(null);
  const [isPrivacyLoading, setIsPrivacyLoading] = useState(false);
  const location = useLocation();
  // Normalize paths by removing leading slash and using consistent format
  const currentPath = location.pathname.replace(/^\//, '');
  const isPrivacyPage = currentPath === "privacy";
  const isTrialNoticePage = currentPath === "trial-notice";
  const isSubscriptionChoicePage = currentPath === "subscription-choice";
  
  // Use a ref to track if this is the initial render to avoid showing loading spinner during quick auth checks
  const isInitialRender = useRef(true);

  // Store a key to control when we should re-check privacy status
  const [privacyCheckKey, setPrivacyCheckKey] = useState(0);
  
  // Function to force refresh privacy status
  const refreshPrivacyStatus = () => {
    setPrivacyCheckKey(prev => prev + 1);
  };
  
  // Make this function available to the window object for debugging
  useEffect(() => {
    // @ts-ignore
    window.__refreshPrivacyStatus = refreshPrivacyStatus;
  }, []);
  
  // Use a ref to track if we should skip the effect during current render cycle
  const [hasCheckedPrivacy, setHasCheckedPrivacy] = useState(false);
  
  useEffect(() => {
    // Skip redundant privacy checks
    const shouldCheckPrivacy = isAuthenticated && !isPrivacyLoading && 
      (hasAcceptedPrivacy === null || privacyCheckKey > 0);
    
    if (!shouldCheckPrivacy) {
      return;
    }
    
    const checkPrivacyStatus = async () => {
      console.log("User is authenticated, checking privacy status...");
      setIsPrivacyLoading(true);
      
      try {
        const response = await getPrivacyStatus();
        console.log("Privacy status response:", response);
        
        // Set state directly without clearing first
        setHasAcceptedPrivacy(response.hasAcceptedPrivacy);
        console.log("Has accepted privacy:", response.hasAcceptedPrivacy);
        setHasCheckedPrivacy(true);
      } catch (error) {
        console.error("Failed to check privacy status:", error);
        // Default to true in case of error to not block access
        console.warn("Defaulting hasAcceptedPrivacy to TRUE due to error");
        setHasAcceptedPrivacy(true);
      } finally {
        setIsPrivacyLoading(false);
      }
    };

    checkPrivacyStatus();
    
    // Reset privacyCheckKey after handling it
    if (privacyCheckKey > 0) {
      setPrivacyCheckKey(0);
    }
  }, [isAuthenticated, isPrivacyLoading, privacyCheckKey, hasAcceptedPrivacy, hasCheckedPrivacy]);
  
  // Reset state when user is not authenticated
  useEffect(() => {
    if (!isAuthenticated && hasAcceptedPrivacy !== null) {
      setHasAcceptedPrivacy(null);
      setHasCheckedPrivacy(false);
      console.log("User is not authenticated, reset privacy acceptance state");
    }
  }, [isAuthenticated, hasAcceptedPrivacy]);

  // Only show loading spinner for initial load or for operations that take a while
  // This prevents disruptive UI flickering during quick auth status checks
  if ((isInitialRender.current && (isAuthLoading || (isAuthenticated && isPrivacyLoading))) || 
      (!isInitialRender.current && ((isAuthLoading && isAuthLoading) || (isAuthenticated && isPrivacyLoading && isPrivacyLoading)))) {
    return <LoadingSpinner />;
  }
  
  // Mark initial render as complete after first render
  if (isInitialRender.current) {
    isInitialRender.current = false;
  }

  // If authentication check is complete and user is not authenticated, redirect to login
  if (!isAuthLoading && !isAuthenticated) {
    console.log("User is not authenticated after auth check, redirecting to login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only proceed with privacy-related redirects if we have determined privacy acceptance status
  if (hasAcceptedPrivacy !== null) {
    // Only redirect to privacy page if:
    // 1. User has NOT accepted privacy AND
    // 2. We're not already on the privacy page
    if (hasAcceptedPrivacy === false && !isPrivacyPage) {
      console.log("User has not accepted privacy policy, redirecting to privacy");
      return <Navigate to="/privacy" state={{ from: location }} replace />;
    }

    // If we're on the privacy page but user has already accepted privacy, redirect to meetings
    if (hasAcceptedPrivacy === true && isPrivacyPage) {
      console.log("User already accepted privacy, redirecting from privacy page to meetings");
      return <Navigate to="/app/meetings" state={{ from: location }} replace />;
    }
  }

  // If we've passed privacy checks and user is authenticated, check subscription status
  if (hasAcceptedPrivacy === true && isAuthenticated) {
    // Only show loading spinner for subscription check during initial load
    // This prevents UI disruption during quick subscription checks
    if (isInitialRender.current && isSubscriptionLoading) {
      return <LoadingSpinner />;
    }
    
    // If subscription data is not available but we're not in loading state,
    // this likely means we haven't requested it yet
    if (!subscription && !isSubscriptionLoading) {
      try {
        // This will trigger the subscription check and cache it
        checkSubscriptionStatus().catch((error: Error) => {
          console.log("Failed to check subscription in ProtectedRoute:", error);
        });
        return <LoadingSpinner />;
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    }

    // Only proceed with subscription checks if we have valid subscription data
    if (subscription) {
      // First priority: Show trial notice if user hasn't seen it yet
      // Skip this check if we're already on the trial notice page or subscription choice page
      if (!subscription.hasSeenTrialNotice && !isTrialNoticePage && !isSubscriptionChoicePage) {
        console.log("User hasn't seen trial notice, redirecting to trial notice");
        return <Navigate to="/trial-notice" state={{ from: location }} replace />;
      }

      // Second priority: Check if trial has expired and user is still on trial status
      // Only show subscription choice when trial is actually expired
      const trialExpired = subscription.daysRemaining <= 0;
      const isFreeOrPaid = subscription.status === 'free' || subscription.status === 'paid';
      const isTrialStatus = subscription.status === 'trial';
      
      // Only show subscription choice if:
      // 1. Trial has expired (days <= 0)
      // 2. User is still on 'trial' status (hasn't chosen free/paid yet)
      // 3. User has already seen the trial notice
      // 4. We're not already on the subscription choice or trial notice pages
      if (trialExpired && isTrialStatus && subscription.hasSeenTrialNotice && 
          !isSubscriptionChoicePage && !isTrialNoticePage) {
        console.log("Trial expired, redirecting to subscription choice");
        return <Navigate to="/subscription-choice" state={{ from: location }} replace />;
      }

      // If we're on trial notice but have already seen it, redirect to original destination
      if (isTrialNoticePage && subscription.hasSeenTrialNotice) {
        console.log("Already seen trial notice, redirecting to meetings");
        return <Navigate to="/app/meetings" state={{ from: location }} replace />;
      }
      
      // Check if user is explicitly trying to change their subscription
      const isChangingSubscription = location.search.includes('change=true');
      
      // Only redirect away from subscription choice if user already has a subscription
      // AND they're not explicitly trying to change their subscription
      if (isSubscriptionChoicePage && isFreeOrPaid && !isChangingSubscription) {
        console.log("Already have a subscription and not changing plan, redirecting to meetings");
        return <Navigate to="/app/meetings" state={{ from: location }} replace />;
      }
    }

    // Moved inside the subscription null check above
  }

  return <>{children}</>;
}
